import { Grid, Skeleton, Typography } from '@mui/material';

import { useEffect } from 'react';

import { useLazyQuery } from '@apollo/client/react';

import { Or2Card } from '@/components/Surface/Or2Card';

import { Editor } from '@/modules/editor/Editor';

import { graphql, type DocumentType } from '@/gql';

import { useStreamAssistantResponse } from '@/modules/assistant/hooks/useStreamAssistantResponse';

import { extractConnectionProps } from '@/utils/queries/queryExtracts';

import { AssistantAiResponseIntercomArticles } from './ai-response/AssistantAiResponseIntercomArticles';
import { AssistantAiResponseActions } from './ai-response/AssistantAiResponseActions';
import { AssistantAiResponseKnowledgeArticles } from './ai-response/AssistantAiResponseKnowledgeArticles';
import {
  ASSISTANT_INTERCOM_SOURCES_RESPONSE_HEADING_TEXT,
  ASSISTANT_KNOWLEDGE_SOURCES_RESPONSE_HEADING_TEXT,
  ASSISTANT_SOURCES_RESPONSE_HEADING_TEXT,
} from './utils';

const ASSISTANT_AI_RESPONSE_BUBBLE_CONVERSATION_ITEM_QUERY = graphql(`
  query AssistantAiResponseBubbleConversationItem(
    $ortooGptConversationItemId: ID!
  ) {
    ortooGptConversationItem(id: $ortooGptConversationItemId) {
      ...ortooGptConversationItemFields
    }
  }
`);

const ASSISTANT_AI_RESPONSE_BUBBLE_ITEM_FRAGMENT = graphql(`
  fragment assistantAiResponseBubbleItemFields on OrtooGptConversationItem {
    _id
    ...assistantAiResponseFeedbackItemFields
    response {
      ...assistantAiResponseKnowledgeArticlesResponseFields
      ...assistantAiResponseIntercomArticlesResponseFields
      responseText
    }
  }
`);

interface Props {
  item: DocumentType<typeof ASSISTANT_AI_RESPONSE_BUBBLE_ITEM_FRAGMENT>;
}

export const AssistantAiResponse = ({ item }: Props) => {
  const { _id: itemId, response } = item ?? {};
  const { responseText } = response ?? {};

  const shouldSkipStreaming = Boolean(responseText);

  const {
    loading,
    serverError,
    clientError,
    response: streamingResponse,
    completed,
  } = useStreamAssistantResponse(item?._id, shouldSkipStreaming);

  const serverErrorMessage = serverError
    ? `Server error: ${serverError}`
    : null;

  const clientErrorMessage = clientError
    ? `Client error: ${clientError?.message}`
    : null;

  const filteredClientErrorMessage = clientErrorMessage
    ? clientErrorMessage.toLowerCase().includes('socket closed')
      ? null
      : clientErrorMessage
    : null;

  const errorMessage = serverErrorMessage || filteredClientErrorMessage;

  const [refetchConversationItem] = useLazyQuery(
    ASSISTANT_AI_RESPONSE_BUBBLE_CONVERSATION_ITEM_QUERY,
  );

  useEffect(() => {
    if (itemId && completed) {
      refetchConversationItem({
        variables: {
          ortooGptConversationItemId: itemId,
        },
      });
    }
  }, [itemId, completed, refetchConversationItem]);

  const knowledgeSources = extractConnectionProps(
    response?.knowledgeSourcesConnection,
  );
  const intercomSources = extractConnectionProps(
    response?.intercomSourcesConnection,
  );

  return (
    <Grid width="100%">
      <Or2Card or2Variant="blank" sx={{ padding: 2 }}>
        <Grid container direction="column" spacing={0}>
          {loading && (
            <Grid container direction="column" spacing={2}>
              <Grid>
                <Typography variant="body1" color="textSecondary">
                  We&apos;re working on your response...
                </Typography>
              </Grid>
              {[...Array(3)].map((_element, key) => (
                <Grid key={key}>
                  <Skeleton width="100%" height={20} />
                </Grid>
              ))}
              <Grid>
                <Skeleton width="80%" height={20} />
              </Grid>
            </Grid>
          )}
          <Grid>
            <Editor
              htmlContent={responseText ? responseText : streamingResponse}
              padding={[2, 0]}
            />
          </Grid>
          {errorMessage && (
            <Grid>
              <Typography variant="body2" color="error">
                {errorMessage}
              </Typography>
            </Grid>
          )}
          {response && (knowledgeSources.length || intercomSources.length) ? (
            <Grid container direction="column" spacing={1} paddingBottom={1}>
              <Typography variant="h6">
                {ASSISTANT_SOURCES_RESPONSE_HEADING_TEXT}
              </Typography>
              {knowledgeSources.length && (
                <>
                  <Typography fontWeight="bold">
                    {ASSISTANT_KNOWLEDGE_SOURCES_RESPONSE_HEADING_TEXT}
                  </Typography>
                  <AssistantAiResponseKnowledgeArticles response={response} />
                </>
              )}
              {intercomSources.length > 0 && (
                <>
                  <Typography fontWeight="bold">
                    {ASSISTANT_INTERCOM_SOURCES_RESPONSE_HEADING_TEXT}
                  </Typography>
                  <AssistantAiResponseIntercomArticles response={response} />
                </>
              )}
              {item && (
                <Grid>
                  <AssistantAiResponseActions item={item} />
                </Grid>
              )}
            </Grid>
          ) : null}
        </Grid>
      </Or2Card>
    </Grid>
  );
};
